import {
  EnvironmentOutlined,
  InfoCircleOutlined,
  MailOutlined,
  PhoneOutlined
} from '@ant-design/icons'
import { getNominative } from '@lmc/common/utils'
import type { Home } from '@lmc/ts-common'
import { theme } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Popover, Tooltip } from 'antd'
import useFeatureFlag from 'hooks/useFeatureFlags'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { openGoogleMaps } from 'screens/Rostering/utils'
import { styled } from 'styled-components'
import DeactivatedIcon from './icons/DeactivatedIcon'
import DeletedIcon from './icons/DeletedIcon'

type InfoIconsProps = {
  address?: string
  email?: string
  phone?: string
  notes?: string
  visit?: boolean
  shift?: boolean
  inactive?: boolean
  deleted?: boolean
  residentStatus?: string
}

const InfoIcons = ({
  address,
  email,
  phone,
  notes,
  visit,
  shift,
  inactive,
  deleted,
  residentStatus
}: InfoIconsProps) => {
  const { t } = useTranslation()

  const getNotesLabel = () => {
    if (shift) {
      return 'SHIFT_NOTES'
    }

    if (visit) {
      return 'VISIT_PREFERENCES'
    }

    return 'ROTA_PREFERENCES'
  }

  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative
  )

  const residentNominative = getNominative(
    preferredPeopleNominative,
    'Resident'
  )

  const flags = useFeatureFlag({
    flagNames: ['rostering_change_management']
  })?.data

  const flagsRosteringChangeManagementEnabled =
    flags?.rostering_change_management?.enabled

  return (
    <IconSection>
      {address && (
        <Popover
          placement="bottomLeft"
          showArrow
          arrow={{ pointAtCenter: true }}
          trigger="hover"
          content={
            <ClickableContentContainer onClick={() => openGoogleMaps(address)}>
              <StyledText level={2}>{t('LOCATION')}</StyledText>
              <UnderlineText>{address}</UnderlineText>
            </ClickableContentContainer>
          }
        >
          <IconWrapper>
            <EnvironmentOutlined />
          </IconWrapper>
        </Popover>
      )}
      {email && (
        <Popover
          placement="bottomLeft"
          showArrow
          arrow={{ pointAtCenter: true }}
          trigger="hover"
          content={
            <ContentContainer>
              <StyledText level={2}>{t('EMAIL')}</StyledText>
              <Text>
                <a href={`mailto:${email}`} style={{ color: '#000000' }}>
                  {email}
                </a>
              </Text>
            </ContentContainer>
          }
        >
          <IconWrapper>
            <MailOutlined />
          </IconWrapper>
        </Popover>
      )}
      {phone && (
        <Popover
          placement="bottomLeft"
          showArrow
          arrow={{ pointAtCenter: true }}
          trigger="hover"
          content={
            <ContentContainer>
              <StyledText level={2}>{t('PHONE_NUMBER')}</StyledText>
              <Text>{phone}</Text>
            </ContentContainer>
          }
        >
          <IconWrapper>
            <PhoneOutlined />
          </IconWrapper>
        </Popover>
      )}
      {notes && (
        <Popover
          placement="bottomLeft"
          showArrow
          arrow={{ pointAtCenter: true }}
          trigger="hover"
          content={
            <ContentContainer>
              <StyledText level={2}>{t(getNotesLabel())}</StyledText>
              <Text>{notes}</Text>
            </ContentContainer>
          }
        >
          <IconWrapper>
            <InfoCircleOutlined />
          </IconWrapper>
        </Popover>
      )}
      {flagsRosteringChangeManagementEnabled && inactive && !deleted && (
        <Tooltip
          color={theme.color.neutral.color3}
          placement="bottomLeft"
          title={
            <TooltipTitle>
              {residentStatus
                ? t('SU_DEACTIVATED_TOOLTIP', {
                    nominative: residentNominative,
                    residentStatus
                  })
                : t('TM_DEACTIVATED_TOOLTIP')}
            </TooltipTitle>
          }
        >
          <IconWrapper>
            <DeactivatedIcon />
          </IconWrapper>
        </Tooltip>
      )}
      {flagsRosteringChangeManagementEnabled && deleted && (
        <Tooltip
          color={theme.color.neutral.color3}
          placement="bottomLeft"
          title={
            <TooltipTitle>
              {residentStatus
                ? t('SU_DELETED_TOOLTIP', {
                    nominative: residentNominative
                  })
                : t('TM_DELETED_TOOLTIP')}{' '}
              <UnderlinedLink
                href="https://support.logmycare.co.uk/en/articles/10233510-how-does-deleting-a-service-user-or-staff-member-impact-your-rota"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('HERE')}
              </UnderlinedLink>
              .
            </TooltipTitle>
          }
        >
          <IconWrapper>
            <DeletedIcon />
          </IconWrapper>
        </Tooltip>
      )}
    </IconSection>
  )
}

const IconSection = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${(props) => props.theme.color.neutral.color5};
`
const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 14px;
  &:hover {
    color: ${(props) => props.theme.color.hover.primary};
  }
`
const StyledText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 8px;
  max-width: 310px;
  cursor: pointer;
`
const ClickableContentContainer = styled(ContentContainer)`
  cursor: pointer;
`
const UnderlineText = styled(Text)`
  text-decoration: underline;
`
const TooltipTitle = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color11};
`
const UnderlinedLink = styled.a`
  text-decoration: underline;
  color: ${(props) => props.theme.color.status.primary};
`

export default InfoIcons
